/**
 * Reload path for a failure the cached shell would otherwise reproduce.
 *
 * Every lazily loaded route lives under a sub-path (`/admin/…`), and those
 * navigations are answered NetworkFirst out of the `pages` cache with a
 * 5-second timeout — see the runtime caching rule in `vite.config.ts`. That
 * cache is what keeps the app rendering offline, but after a deploy its entries
 * still reference asset names nginx has already dropped. So a plain reload on a
 * slow connection can time out, fall back to the stale shell, and land the user
 * right back on the build that just failed to load a chunk.
 *
 * Hence: evict the shell first — but only once the network has been shown to
 * answer with a fresh `index.html`. With no connection that same cache is the
 * only thing between the user and the browser's own error page, and clearing it
 * would turn a recoverable in-app error screen into a dead tab.
 */

/** Runtime cache holding navigation responses. Shared with `vite.config.ts`. */
export const PAGES_CACHE_NAME = 'pages';

// Shorter than the navigation rule's own 5s timeout — a probe that outlives the
// fallback it is meant to prevent tells us nothing worth waiting for.
const PROBE_TIMEOUT_MS = 3_000;

/** Reloads the document, dropping the cached shell first when that is safe. */
export function reloadWithoutCachedShell(): void {
  void dropCachedShell()
    .catch(() => undefined)
    .then(() => window.location.reload());
}

async function dropCachedShell(): Promise<void> {
  // Nothing controls the page (dev, Capacitor native, a first uncached load):
  // the reload cannot be answered from cache, so there is nothing to evict.
  if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;
  if (!('caches' in window) || navigator.onLine === false) return;

  // The unique query keeps this request out of both service-worker routes — the
  // precache holds `/index.html` under a key of its own, and the navigation rule
  // only matches `mode: 'navigate'` — so it measures the network, not the cache.
  const fresh = await fetch(`/index.html?shell=${Date.now()}`, {
    cache: 'no-store',
    signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
  });
  if (!fresh.ok) return;
  await caches.delete(PAGES_CACHE_NAME);
}
